<template>
    <modal-wrapper modalClass="modal" :modalName="name" @before-open="beforeOpen">
        <div class="modal__header">
            <h1>{{$t('modals.upload_document.title')}}</h1>
        </div>
        <div class="modal__content no-side-margins">
            <div v-if="uploadErrors.length" class="alert--error" >
                <span v-for="err in uploadErrors" :key="err"> {{ err }} </span>
            </div>
            <form enctype="multipart/form-data" class="form" novalidate>
                <div class="form__item">
                    <i class="form__icon icons icon-bell"></i>
                    <vue-select v-model="document.action" :options="actions" label="name" class="form__select"
                              :class="{ 'has-value': document.action }">
                        <template slot="no-options">{{$t('forms.common.no_results')}}</template>
                    </vue-select>
                    <label class="form__label">{{$t('modals.upload_document.action_type')}}</label>
                </div>
                <div class="form__item">
                    <i class="form__icon icons icon-drawer"></i>
                    <vue-select v-model="document.type" :options="types" label="name" class="form__select"
                              :class="{ 'has-value': document.type }">
                        <template slot="no-options">{{$t('forms.common.no_results')}}</template>
                    </vue-select>
                    <label class="form__label">{{$t('modals.upload_document.category')}}</label>
                </div>

                <div class="form__item">
                    <div class="checkbox">
                        <input type="checkbox" class="checkbox__cb" id="file_send_client" v-model="document.send_client" @change="preventSendClientChange"/>
                        <label for="file_send_client" class="checkbox__label"> {{$t('modals.upload_document.send_client')}}{{clientName ? ': ' + clientName : ''}}</label>
                    </div>

                    <confirmation-alert
                        :show="showSendClientAlert"
                        message="modals.upload_document.alert_send_client"
                        @confirm="confirmSendClient"
                        @reject="rejectSendClient"/>


                    <div v-if="document.send_client" class="checkbox">
                        <input type="checkbox" class="checkbox__cb" id="file_approve_client" v-model="document.approve_client"/>
                        <label for="file_approve_client" class="checkbox__label"> {{$t('modals.upload_document.approve_client')}}</label>
                    </div>
                </div>

                <div class="form__item">
                    <div class="checkbox">
                        <input type="checkbox" class="checkbox__cb" id="file_send_employer" v-model="document.send_employer" @change="preventSendEmployerChange"/>
                        <label for="file_send_employer" class="checkbox__label"> {{$t('modals.upload_document.send_employer')}}{{contactName ? ': ' + contactName : ''}}</label>
                    </div>

                    <confirmation-alert
                        :show="showSendEmployerAlert"
                        message="modals.upload_document.alert_send_employer"
                        @confirm="confirmSendEmployer"
                        @reject="rejectSendEmployer"/>


                    <div v-if="document.send_employer" class="checkbox">
                        <input type="checkbox" class="checkbox__cb" id="file_approve_employer" v-model="document.approve_employer"/>
                        <label for="file_approve_employer" class="checkbox__label"> {{$t('modals.upload_document.approve_employer')}}</label>
                    </div>
                </div>

                <dropzone-with-loading-button :uploading="documentUploading" @fileSelectedEvent="onDocumentSelected($event)" />
                {{selectedFileToUpload ? selectedFileToUpload.name : ''}}

                <div>{{$t('common.or')}}</div>

                <loading-button @submited="openTemplateModal"
                                :loading="documentUploading && hydrating"
                                :text="$t('common.hydrate_template')"/>
            </form>
        </div>
        <div class="modal__footer">
            <div class="actions">
                <button v-if="selectedFileToUpload" class="actions__action--save pull-right" @click.prevent="uploadDocument(selectedFileToUpload)">{{$t('common.upload_file')}}</button>
                <button class="actions__action--cancel" type="button" @click.prevent="$modal.hide(name)">{{$t('modals.common.cancel')}}</button>
            </div>
        </div>
        <hydrate-template-modal @hydrated="onTemplateHydrated"/>
    </modal-wrapper>
</template>
<script>
    import Vue from 'vue';
    import {Component} from 'vue-property-decorator';
    import ModalWrapper from '@/components/general/ModalWrapper';
    import ConfirmationAlert from '@/components/general/ConfirmationAlert';
    import DropzoneWithLoadingButton from '@/components/general/DropzoneWithLoadingButton';

    import {GET_DOCUMENTS} from '@/store/modules/documents';
    import {LOAD_FILE_RESOURCES} from '@/store/modules/file-resources';

    import HydrateTemplateModal from '@/components/modals/dashboard/HydrateTemplateModal';
    import LoadingButton from '@/components/general/LoadingButton';

    @Component({
        components: {
            ConfirmationAlert,
            DropzoneWithLoadingButton,
            HydrateTemplateModal,
            LoadingButton,
        }
    })
    export default class DocumentUploadModal extends Vue {
        name = 'document-upload-modal';
        documentUploading = false;
        hydrating = false;
        uploadErrors = [];
        document = {
            send_client: false,
            approve_client: false,
            send_employer: false,
            approve_employer: false,
        };

        selectedFileToUpload = null;

        created() {
            this.$store.dispatch(LOAD_FILE_RESOURCES);
        }

        beforeOpen() {
            this.uploadErrors = [];
            this.documentUploading = false;
            this.selectedFileToUpload = null;

            this.showSendClientAlert = false;
            this.showSendEmployerAlert = false;

            this.document = {
                action: this.actions[0],
                type: this.types[0],
                send_client: false,
                approve_client: false,
                send_employer: false,
                approve_employer: false
            };
        }

        get loading() {
            return this.$store.state.file_resources.loading;
        }

        get actions() {
            return this.$store.state.file_resources.file_actions;
        }

        get types() {
            return this.$store.state.file_resources.file_types;
        }

        get trajectory() {
            return this.$store.state.trajectory.trajectory;
        }

        get clientName() {
            return this.$store.state.trajectory.trajectory.client.name;
        }

        get contactName() {
            return this.$store.state.trajectory.trajectory.contact_name;
        }

        showSendClientAlert = false;

        preventSendClientChange() {
            if (this.document.send_client) {
                this.document.send_client = false;
                this.showSendClientAlert = true;
            }
        }

        confirmSendClient() {
            this.document.send_client = true;
            this.showSendClientAlert = false;
        }

        rejectSendClient() {
            this.document.send_client = false;
            this.showSendClientAlert = false;
        }

        showSendEmployerAlert = false;

        preventSendEmployerChange() {
            if (this.document.send_employer) {
                this.document.send_employer = false;
                this.showSendEmployerAlert = true;
            }
        }

        confirmSendEmployer() {
            this.document.send_employer = true;
            this.showSendEmployerAlert = false;
        }

        rejectSendEmployer() {
            this.document.send_employer = false;
            this.showSendEmployerAlert = false;
        }


        onDocumentSelected(e) {
            var files = e.target.files || e.dataTransfer.files;
            if (!files.length)
                return;

            this.selectedFileToUpload = files[0];
        }

        uploadDocument(file) {
            if (!this.document.type.id || !this.document.action.id) return;
            const formData = new FormData();
            formData.append('file', file, file.name);
            this.hydrating = false;
            this.performUploadRequest(formData);
        }

        openTemplateModal() {
            if (this.documentUploading) return;
            this.$modal.show('hydrate-template-modal', {
                document: this.document
            });
        }

        onTemplateHydrated(hydratedDocument) {
            if (!this.document.type.id || !this.document.action.id) return;
            const formData = new FormData();
            formData.append('document_id', hydratedDocument.id);
            this.hydrating = true;
            this.performUploadRequest(formData);
        }

        performUploadRequest(formData) {
            formData.append('send_client', this.document.send_client ? 1 : 0);
            formData.append('approve_client', this.document.send_client && this.document.approve_client ? 1 : 0);
            formData.append('send_employer', this.document.send_employer ? 1 : 0);
            formData.append('approve_employer', this.document.send_employer && this.document.approve_employer ? 1 : 0);

            formData.append('trajectory_file_type_id', this.document.type.id);
            formData.append('trajectory_file_action_id', this.document.action.id);

            this.uploadErrors = [];
            this.documentUploading = true;
            this.axios.post(`trajectories/${this.trajectory.id}/files`, formData)
                .then(res => {
                    this.$notify.success(this.$t('notify.upload_success'));
                    this.$modal.hide(this.name);
                })
                .catch(
                    err => {
                        if (err.status === 422 && err.errors) {
                            this.uploadErrors = err.errors;
                            this.$notify.error(this.$t('common.validation_error'));
                        } else {
                            this.$notify.error(this.$t('notify.upload_error'));
                        }
                    }
                ).then(() => {
                    this.documentUploading = false;
                    this.$store.dispatch(GET_DOCUMENTS, {});
                });
        }
    }
</script>



// WEBPACK FOOTER //
// src/components/modals/dashboard/DocumentUploadModal.vue